import {Injectable} from '@angular/core';
import {map, mergeMap} from 'rxjs/operators';
import {Subject} from 'rxjs';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {Store} from '@ngrx/store';
import {AuthService, TeamsService} from '../services';
import {AppState} from '../reducers';
import {
    CreateUserAction,
    DeleteManyUsersAction,
    LoadUsersAction,
    UpdateUserAction,
    UserActionTypes,
    UserCreatedAction,
    UsersBusyAction,
    UsersError,
    UsersLoadedAction,
    UsersLoadingAction, UserUpdatedAction
} from '../actions/user.actions';
import {UserModel} from '../models/user.model';
import {FileSystemFileEntry} from 'ngx-file-drop';
import {environment} from '../../../environments/environment';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {CurrentUserLoadedAction} from '../actions/auth.actions';

@Injectable()
export class UserEffects {
    showPageLoadingDispatcher = new UsersLoadingAction({isLoading: true});
    showActionLoadingDispatcher = new UsersBusyAction({isLoading: true});
    hideActionLoadingDispatcher = new UsersBusyAction({isLoading: false});

    constructor(
        private actions$: Actions,
        private auth: AuthService,
        private teamsService: TeamsService,
        private store: Store<AppState>,
        private http: HttpClient) {
    }

    loadUsersPage$ = createEffect(() =>
        this.actions$.pipe(
            ofType<LoadUsersAction>(UserActionTypes.LOAD_USERS),
            mergeMap(({payload, reset}) => {
                if (reset) {
                    this.store.dispatch(new UsersLoadedAction({
                        users: [],
                        totalCount: 0,
                    }));
                }
                this.store.dispatch(this.showPageLoadingDispatcher);
                if (payload.page.filter.teamId !== undefined) {
                    const teamId = payload.page.filter.teamId;
                    const searchText = payload.page.filter.searchText.length > 0 ? payload.page.filter.searchText : undefined;
                    const limit = payload.page.pageSize;
                    const offset = payload.page.pageNumber * limit;
                    const includeDeleted = payload.page.filter.includeDeleted || false;
                    return this.teamsService.getTeamUsers(teamId, searchText, limit, offset, includeDeleted);
                }
                return this.auth.findUsers(payload.page);
            }),
            map(response => {
                return new UsersLoadedAction({
                    users: response.items,
                    totalCount: response.totalCount,
                });
            })
        )
    );

    deleteUsers$ = createEffect(() =>
        this.actions$.pipe(
            ofType<DeleteManyUsersAction>(UserActionTypes.MANY_USERS_DELETED),
            mergeMap(({payload}) => {
                    this.store.dispatch(this.showActionLoadingDispatcher);
                    return this.auth.deleteManyUsers(payload.ids);
                }
            ),
            map(() => {
                return this.hideActionLoadingDispatcher;
            })
        )
    );

    updateUser$ = createEffect(() =>
        this.actions$.pipe(
            ofType<UpdateUserAction>(UserActionTypes.UPDATE_USER),
            mergeMap(({payload}) => {
                this.store.dispatch(this.showActionLoadingDispatcher);
                const subject = new Subject<UserModel>();
                this.auth.updateUser(
                    payload.user,
                    payload.files,
                    payload.addWatcherIds,
                    payload.removeWatcherIds,
                    payload.addUserGroupIds,
                    payload.removeUserGroupIds).subscribe((user: UserModel) => {
                    if (payload.files.length > 0) {
                        const droppedFile = payload.files[0];
                        if (droppedFile.fileEntry.isFile) {
                            const fileEntry = droppedFile.fileEntry as FileSystemFileEntry;
                            fileEntry.file((file: File) => {
                                const formData = new FormData();
                                formData.append('file', file, droppedFile.relativePath);
                                const userToken = localStorage.getItem(environment.authTokenKey);
                                const headers = new HttpHeaders({
                                    Authorization: 'Bearer ' + userToken
                                });
                                this.http.post(
                                    `${environment.pathToAPI}/api/v1/users/${user.id}/pic`,
                                    formData,
                                    {headers}).subscribe((data: UserModel) => {
                                    subject.next(data);
                                });
                            });
                        }
                    } else {
                        subject.next(user);
                    }
                }, (res) => {
                    this.store.dispatch(new UsersError({error_code: res.error.error_code, message: res.error.message}));
                });
                return subject.pipe(map((user: UserModel) => {
                    this.store.dispatch(new UserUpdatedAction({user}));
                    if (payload.current) {
                        const u = Object.assign({}, user);
                        this.auth.resolveRole(u);
                        this.store.dispatch(new CurrentUserLoadedAction({user: u}));
                    }
                }));
            }),
            map(() => {
                return this.hideActionLoadingDispatcher;
            })
        )
    );

    createUser$ = createEffect(() =>
        this.actions$.pipe(
            ofType<CreateUserAction>(UserActionTypes.CREATE_USER),
            mergeMap(({payload}) => {
                this.store.dispatch(this.showActionLoadingDispatcher);
                const subject = new Subject<UserModel>();
                this.auth.createUser(payload.user, payload.files, payload.addWatcherIds, payload.addUserGroupIds).subscribe((user: UserModel) => {
                    if (payload.files.length > 0) {
                        const droppedFile = payload.files[0];
                        if (droppedFile.fileEntry.isFile) {
                            const fileEntry = droppedFile.fileEntry as FileSystemFileEntry;
                            fileEntry.file((file: File) => {
                                const formData = new FormData();
                                formData.append('file', file, droppedFile.relativePath);
                                const userToken = localStorage.getItem(environment.authTokenKey);
                                const headers = new HttpHeaders({
                                    Authorization: 'Bearer ' + userToken
                                });
                                this.http.post(
                                    `${environment.pathToAPI}/api/v1/users/${user.id}/pic`,
                                    formData,
                                    {headers}).subscribe((data: {response: UserModel}) => {
                                    subject.next(data.response);
                                });
                            });
                        }
                    } else {
                        subject.next(user);
                    }
                }, (res) => {
                    this.store.dispatch(new UsersError({error_code: res.error.error_code, message: res.error.message}));
                });
                return subject.pipe(map((user: UserModel) => {
                    this.store.dispatch(new UserCreatedAction({user}));
                }));
            }),
            map(() => {
                return this.hideActionLoadingDispatcher;
            })
        )
    );
}
